import { useEffect, useState, useCallback } from "react";
import { Paper, Typography, Box, useTheme } from "@mui/material";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import GrainIcon from "@mui/icons-material/Grain";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import { tokens } from "../../../theme";

interface WeatherData {
  city: string;
  temperature: number;
  weather: string;
  icon: string;
}

const REFRESH_INTERVAL_MINUTES = 10;
const FALLBACK_CITY = "Harare";
const FALLBACK_LAT = -17.8252;
const FALLBACK_LON = 31.0335;

// Known branch/city locations, used to label the widget without an external
// reverse-geocoding call (avoids CORS/rate-limit failures from open-meteo).
const KNOWN_CITIES: { name: string; lat: number; lon: number }[] = [
  { name: "Harare", lat: -17.8252, lon: 31.0335 },
  { name: "Bulawayo", lat: -20.15, lon: 28.5833 },
  { name: "Mutare", lat: -18.9707, lon: 32.6709 },
  { name: "Gweru", lat: -19.45, lon: 29.8167 },
  { name: "Victoria Falls", lat: -17.9243, lon: 25.8567 },
];

const nearestCity = (latitude: number, longitude: number) => {
  let closest = KNOWN_CITIES[0];
  let minDistance = Infinity;

  for (const city of KNOWN_CITIES) {
    const dLat = city.lat - latitude;
    const dLon = city.lon - longitude;
    const distance = dLat * dLat + dLon * dLon;
    if (distance < minDistance) {
      minDistance = distance;
      closest = city;
    }
  }

  return closest.name;
};

export default function WeatherWidget() {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const getWeatherIcon = (iconCode?: string) => {
    if (!iconCode) {
      return <CloudOutlinedIcon sx={{ color: "text.secondary", fontSize: 16 }} />;
    }

    const type = iconCode.slice(0, 2);

    switch (type) {
      case "01":
        return <WbSunnyIcon sx={{ color: "#FFD700", fontSize: 16 }} />;
      case "02":
      case "03":
      case "04":
        return <CloudOutlinedIcon sx={{ color: "text.secondary", fontSize: 16 }} />;
      case "09":
      case "10":
        return <GrainIcon sx={{ color: "#4FC3F7", fontSize: 16 }} />;
      case "11":
        return <FlashOnIcon sx={{ color: "#FFCA28", fontSize: 16 }} />;
      case "13":
        return <AcUnitIcon sx={{ color: "#B3E5FC", fontSize: 16 }} />;
      default:
        return <CloudOutlinedIcon sx={{ color: "text.secondary", fontSize: 16 }} />;
    }
  };

  const mapOpenMeteoCode = (code: number) => {
    if (code === 0) return { weather: "Clear", icon: "01d" };
    if (code >= 1 && code <= 3)
      return { weather: "Partly cloudy", icon: "03d" };
    if (code >= 45 && code <= 48) return { weather: "Foggy", icon: "04d" };
    if (code >= 51 && code <= 67) return { weather: "Rainy", icon: "10d" };
    if (code >= 71 && code <= 77) return { weather: "Snowy", icon: "13d" };
    if (code >= 80 && code <= 82) return { weather: "Showers", icon: "09d" };
    if (code >= 95 && code <= 99)
      return { weather: "Thunderstorm", icon: "11d" };
    return { weather: "Cloudy", icon: "04d" };
  };

  const fetchWeatherByCoords = useCallback(
    async (latitude: number, longitude: number) => {
      try {
        const weatherResponse = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&temperature_unit=celsius`,
        );
        const weatherJson = await weatherResponse.json();

        const city = nearestCity(latitude, longitude);
        const mapped = mapOpenMeteoCode(weatherJson.current.weather_code);

        setWeather({
          city,
          temperature: Math.round(weatherJson.current.temperature_2m),
          weather: mapped.weather,
          icon: mapped.icon,
        });
      } catch (error) {
        console.error("Weather fetch failed", error);
        setWeather({
          city: FALLBACK_CITY,
          temperature: 0,
          weather: "Unavailable",
          icon: "",
        });
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const fetchWeather = useCallback(async () => {
    setLoading(true);

    if (!navigator.geolocation) {
      fetchWeatherByCoords(FALLBACK_LAT, FALLBACK_LON);
      return;
    }

    // Best-effort: skip the geolocation call when already denied / policy-blocked
    // to avoid a console violation, falling back to the default coords.
    const permission = await navigator.permissions
      ?.query({ name: "geolocation" as PermissionName })
      .catch(() => undefined);
    if (permission?.state === "denied") {
      fetchWeatherByCoords(FALLBACK_LAT, FALLBACK_LON);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        fetchWeatherByCoords(
          position.coords.latitude,
          position.coords.longitude,
        );
      },
      () => {
        fetchWeatherByCoords(FALLBACK_LAT, FALLBACK_LON);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5 * 60 * 1000,
      },
    );
  }, [fetchWeatherByCoords]);

  useEffect(() => {
    fetchWeather();

    const interval = setInterval(
      fetchWeather,
      REFRESH_INTERVAL_MINUTES * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [fetchWeather]);

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          p: 2,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: colors.primary[900],
        }}
      >
        <Typography variant="caption" color={colors.primary[300]}>
          Loading...
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        containerType: "inline-size",
        borderRadius: "16px",
        p: 1.5,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.primary[900],
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        {getWeatherIcon(weather?.icon)}
        <Typography
          variant="caption"
          color={colors.primary[300]}
          fontWeight={500}
          noWrap
          sx={{ fontSize: "clamp(0.75rem, 3cqw, 1rem)" }}
        >
          {weather?.weather || "--"}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          fontWeight={700}
          color={colors.primary[400]}
          sx={{
            fontSize: "clamp(3.5rem, 10cqw, 4rem)",
            lineHeight: 1,
            letterSpacing: "-2px",
          }}
        >
          {weather?.temperature ?? "--"}
        </Typography>
        <Typography
          color={colors.primary[300]}
          sx={{
            fontSize: "clamp(2rem, 5cqw, 2.5rem)",
            lineHeight: 1,
            alignSelf: "flex-start",
            mt: "4px",
          }}
        >
          °
        </Typography>
      </Box>

      <Typography
        variant="body2"
        color={colors.primary[700]}
        fontWeight={600}
        noWrap
        sx={{ mt: 0.5, fontSize: "clamp(0.8rem, 3.5cqw, 1rem)" }}
      >
        {weather?.city || "--"}
      </Typography>
    </Paper>
  );
}
